import React, { useState } from 'react';
import { Play, Download, Shuffle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  callout: { background: 'rgba(141,184,122,0.1)', border: '1px solid rgba(141,184,122,0.25)', borderRadius: 10, padding: '1.25rem', marginBottom: '1.5rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6 },
  calloutTitle: { fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--primary)', marginBottom: 6 },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'David K.', primaryCategory: 'Espresso', crossSell: 'Matcha Latte', confidence: 0.76, aovImpact: '+$5.50', insight: 'Espresso lovers who try matcha retain at 2x rate' },
  { name: 'Sarah M.', primaryCategory: 'Coffee', crossSell: 'Wellness Shots', confidence: 0.72, aovImpact: '+$4.00', insight: 'Health-conscious morning routine' },
  { name: 'Alex T.', primaryCategory: 'Cold Brew', crossSell: 'Iced Matcha', confidence: 0.81, aovImpact: '+$5.00', insight: 'Seasonal shift pattern detected' },
  { name: 'Jordan R.', primaryCategory: 'Tea', crossSell: 'Oat Milk Latte', confidence: 0.68, aovImpact: '+$4.75', insight: 'Plant-based preference alignment' },
  { name: 'Rachel W.', primaryCategory: 'Espresso', crossSell: 'Chai Latte', confidence: 0.64, aovImpact: '+$4.50', insight: 'Afternoon variety seeker' },
  { name: 'Michael C.', primaryCategory: 'Drip Coffee', crossSell: 'Pastry Subscription', confidence: 0.70, aovImpact: '+$6.00', insight: 'Daily ritual expansion' },
  { name: 'Lisa B.', primaryCategory: 'Matcha', crossSell: 'Turmeric Latte', confidence: 0.73, aovImpact: '+$5.25', insight: 'Wellness category explorer' },
  { name: 'Tom H.', primaryCategory: 'Cappuccino', crossSell: 'Cold Brew (summer)', confidence: 0.77, aovImpact: '+$3.75', insight: 'Seasonal repertoire builder' },
];

export default function CrossSellRecommendation() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('confidence');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/cross-sell');
      setResults(res.data?.results || fallbackData);
      toast.success('Cross-sell model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Cross-sell model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Cross-Sell Recommendation</h1>
      <p style={s.desc}>
        Recommends cross-category products based on taste profiles, purchase patterns, and look-alike modeling.
        Expands each customer's product repertoire to deepen engagement.
      </p>

      <div style={s.callout}>
        <div style={s.calloutTitle}><Shuffle size={16} style={{ marginRight: 6, verticalAlign: -2 }} />The Espresso-to-Matcha Pipeline</div>
        Espresso-first customers who try matcha retain at 2x the rate of single-category drinkers.
        The crossover typically happens in spring/summer as customers seek lighter, cooler alternatives.
        A well-timed "Meet your matcha" push notification converts at 18% — 3x the baseline cross-sell rate.
      </div>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'cross_sell')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['primaryCategory','Primary Category'],['crossSell','Cross-Sell'],['confidence','Confidence'],['aovImpact','AOV Impact'],['insight','Insight']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.primaryCategory}</td>
                  <td style={{ ...s.td, color: 'var(--primary)', fontWeight: 500 }}>{row.crossSell}</td>
                  <td style={s.td}>{(row.confidence * 100).toFixed(0)}%</td>
                  <td style={{ ...s.td, color: 'var(--primary)' }}>{row.aovImpact}</td>
                  <td style={s.td}>{row.insight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
